import { useEffect, useRef } from 'react'
import type { RefObject } from 'react'
import mapboxgl, { Map } from 'mapbox-gl'
import MapboxGeocoder, { Result } from '@mapbox/mapbox-gl-geocoder'
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css'
import './use-map-geocoder.css'

type OnResult = (lng: number, lat: number, searchLabel: string | null) => void

// Mapbox Geocoder plugin, with our own 'result' listener since GeocoderButton doesn't expose one.
export function useMapGeocoder(
  map: Map | null,
  containerRef: RefObject<HTMLDivElement>,
  onResult: OnResult,
) {
  // Ref avoids rebuilding the widget (and losing input focus) when onResult's identity changes.
  const onResultRef = useRef(onResult)
  useEffect(() => {
    onResultRef.current = onResult
  }, [onResult])

  useEffect(() => {
    if (!map || !containerRef.current) return

    const geocoder = new MapboxGeocoder({
      accessToken: mapboxgl.accessToken as string,
      // @ts-expect-error - @types/mapbox__mapbox-gl-geocoder's mapboxgl type predates mapbox-gl v3
      mapboxgl,
      placeholder: 'Search an address or suburb',
      countries: 'nz',
      // marker-sync hook already owns the pin and camera
      marker: false,
      flyTo: false,
    })

    const container = containerRef.current
    while (container.firstChild) container.firstChild.remove()
    container.appendChild(geocoder.onAdd(map))
    geocoder.clear() // Also focuses the input.

    const handleResult = (e: { result: Result }) => {
      const [lng, lat] = e.result.center
      onResultRef.current(lng, lat, e.result.place_name)
    }
    geocoder.on('result', handleResult)

    return () => {
      geocoder.off('result', handleResult)
      geocoder.onRemove()
    }
    // containerRef is stable for the component's lifetime — excluded deliberately
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map])
}
